import { useCallback, useEffect, useState } from 'react';
import { scheduledCommandsApi } from '../services/scheduledCommandsApi';
import type { TargetCatalog } from '../types/scheduledCommands';

/**
 * The servers and clusters a schedule can target, as the bot's dispatcher sees
 * them (its copy of the modpack configs, plus the version each server last
 * reported).
 *
 * The list changes when a pack launches, not while someone is editing, so one
 * copy is shared by every picker on the page and kept for a minute.
 */
const FRESH_MS = 60_000;

let cached: { at: number; catalog: TargetCatalog } | null = null;
let inflight: Promise<TargetCatalog> | null = null;

function load(force: boolean): Promise<TargetCatalog> {
  if (!force && cached && Date.now() - cached.at < FRESH_MS) return Promise.resolve(cached.catalog);
  if (!inflight) {
    inflight = scheduledCommandsApi
      .targets()
      .then((catalog) => {
        cached = { at: Date.now(), catalog };
        return catalog;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export interface ScheduleTargetsState {
  catalog: TargetCatalog | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useScheduleTargets(): ScheduleTargetsState {
  const [catalog, setCatalog] = useState<TargetCatalog | null>(cached?.catalog ?? null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback((force: boolean) => {
    let live = true;
    setLoading(true);
    load(force)
      .then((next) => {
        if (!live) return;
        setCatalog(next);
        setError(null);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'The server list could not be loaded.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => run(false), [run]);

  const reload = useCallback(() => {
    run(true);
  }, [run]);

  return { catalog, loading, error, reload };
}
